/**
 * Admin Deposits Management
 * View deposit history with filtering and statistics
 */

import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';

interface Deposit {
  id: string;
  txHash: string | null;
  fromAddress: string | null;
  amount: number;
  memo: string | null;
  status: string;
  processedAt: string | null;
  adminNote: string | null;
  createdAt: string;
  user: {
    id: string;
    firstName: string;
    lastName: string | null;
    telegramUserId: number;
  } | null;
  order: {
    id: string;
    memo: string;
    asset: { name: string } | null;
  } | null;
}

interface DepositStats {
  total: { amount: number; count: number };
  today: { amount: number; count: number };
  week: { amount: number; count: number };
  month: { amount: number; count: number };
  pending: number;
}

type FilterStatus = 'all' | 'completed' | 'pending' | 'manual_review' | 'expired';

export function AdminDeposits() {
  const [deposits, setDeposits] = useState<Deposit[]>([]);
  const [stats, setStats] = useState<DepositStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const fetchDeposits = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const status = filter === 'all' ? undefined : filter;
      const res = await api.getAdminDeposits({ page, limit: 30, status, search: search || undefined });
      setDeposits(res.data.deposits || []);
      setTotalPages(res.data.pagination?.pages || 1);
    } catch (err) {
      setError('Failed to load deposits');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filter, page, search]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.getAdminDepositsStats();
      setStats(res.data);
    } catch (err) {
      console.error('Failed to load deposit stats', err);
    }
  }, []);

  useEffect(() => {
    fetchDeposits();
  }, [fetchDeposits]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return '#10b981';
      case 'pending': return '#f59e0b';
      case 'manual_review': return '#8b5cf6';
      case 'expired': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getStatusEmoji = (status: string) => {
    switch (status) {
      case 'completed': return '✅';
      case 'pending': return '⏳';
      case 'manual_review': return '👁️';
      case 'expired': return '⏰';
      default: return '❓';
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a1628 0%, #000814 100%)',
      color: '#fff',
      padding: '16px',
    }}>
      {/* Stats Row */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '8px',
          marginBottom: '16px',
        }}>
          <div style={{
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '10px',
            padding: '12px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '11px', color: '#10b981', marginBottom: '4px' }}>Total Deposits</div>
            <div style={{ fontSize: '18px', fontWeight: 700 }}>${stats.total.amount.toFixed(2)}</div>
            <div style={{ fontSize: '10px', color: '#6b7280' }}>{stats.total.count} txns</div>
          </div>
          <div style={{
            background: 'rgba(59, 130, 246, 0.1)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '10px',
            padding: '12px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '11px', color: '#60a5fa', marginBottom: '4px' }}>Today</div>
            <div style={{ fontSize: '18px', fontWeight: 700 }}>${stats.today.amount.toFixed(2)}</div>
            <div style={{ fontSize: '10px', color: '#6b7280' }}>{stats.today.count} txns</div>
          </div>
          <div style={{
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.3)',
            borderRadius: '10px',
            padding: '12px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '11px', color: '#f59e0b', marginBottom: '4px' }}>This Week</div>
            <div style={{ fontSize: '18px', fontWeight: 700 }}>${stats.week.amount.toFixed(2)}</div>
            <div style={{ fontSize: '10px', color: '#6b7280' }}>{stats.week.count} txns</div>
          </div>
          <div style={{
            background: 'rgba(139, 92, 246, 0.1)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '10px',
            padding: '12px',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '11px', color: '#8b5cf6', marginBottom: '4px' }}>Pending Review</div>
            <div style={{ fontSize: '18px', fontWeight: 700 }}>{stats.pending}</div>
          </div>
        </div>
      )}

      {/* Search */}
      <div style={{ marginBottom: '12px' }}>
        <input
          type="text"
          placeholder="Search by memo, tx hash, address..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          style={{
            width: '100%',
            padding: '10px 14px',
            borderRadius: '10px',
            border: '1px solid rgba(255,255,255,0.15)',
            background: 'rgba(255,255,255,0.05)',
            color: '#fff',
            fontSize: '13px',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Filter tabs */}
      <div style={{
        display: 'flex',
        gap: '6px',
        marginBottom: '16px',
        overflowX: 'auto',
      }}>
        {(['all', 'completed', 'pending', 'manual_review', 'expired'] as FilterStatus[]).map(s => (
          <button
            key={s}
            onClick={() => { setFilter(s); setPage(1); }}
            style={{
              padding: '6px 12px',
              borderRadius: '16px',
              border: filter === s ? '1px solid #3b82f6' : '1px solid rgba(255,255,255,0.1)',
              background: filter === s ? 'rgba(59,130,246,0.3)' : 'rgba(255,255,255,0.05)',
              color: filter === s ? '#60a5fa' : '#9ca3af',
              fontSize: '12px',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            {s === 'all' ? '📋 All' :
             s === 'completed' ? '✅ Completed' :
             s === 'pending' ? '⏳ Pending' :
             s === 'manual_review' ? '👁️ Review' : '⏰ Expired'}
          </button>
        ))}
      </div>

      {error && (
        <div style={{
          background: 'rgba(239,68,68,0.15)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: '8px',
          padding: '12px',
          marginBottom: '16px',
          color: '#fca5a5',
          fontSize: '13px',
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          Loading...
        </div>
      ) : deposits.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
          <div>No deposits found</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {deposits.map(d => (
            <div key={d.id} style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '12px',
              padding: '14px',
            }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '18px' }}>💰</span>
                  <div style={{ fontWeight: 700, fontSize: '16px' }}>
                    {d.amount.toFixed(4)} USDT
                  </div>
                </div>
                <span style={{
                  padding: '3px 8px',
                  borderRadius: '10px',
                  fontSize: '11px',
                  fontWeight: 600,
                  background: `${getStatusColor(d.status)}20`,
                  color: getStatusColor(d.status),
                  border: `1px solid ${getStatusColor(d.status)}40`,
                }}>
                  {getStatusEmoji(d.status)} {d.status}
                </span>
              </div>

              {/* Details */}
              <div style={{ fontSize: '12px', color: '#9ca3af' }}>
                {d.user && (
                  <div style={{ marginBottom: '3px' }}>
                    👤 {d.user.firstName} {d.user.lastName || ''} (ID: {d.user.telegramUserId})
                  </div>
                )}
                {d.memo && (
                  <div style={{ marginBottom: '3px' }}>
                    📝 Memo: <span style={{ color: '#60a5fa' }}>{d.memo}</span>
                  </div>
                )}
                {d.txHash && (
                  <div style={{ marginBottom: '3px', wordBreak: 'break-all' }}>
                    🔗 TX: <span style={{ color: '#60a5fa' }}>{d.txHash.slice(0, 20)}...</span>
                  </div>
                )}
                {d.fromAddress && (
                  <div style={{ marginBottom: '3px', wordBreak: 'break-all' }}>
                    📬 From: <span style={{ color: '#60a5fa' }}>{d.fromAddress.slice(0, 20)}...</span>
                  </div>
                )}
                {d.order?.asset && (
                  <div style={{ marginBottom: '3px' }}>
                    🎁 Asset: {d.order.asset.name}
                  </div>
                )}
                <div>
                  🕐 {new Date(d.createdAt).toLocaleString()}
                </div>
                {d.adminNote && (
                  <div style={{ marginTop: '4px', color: '#fbbf24' }}>
                    📌 Note: {d.adminNote}
                  </div>
                )}
              </div>
            </div>
          ))}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  background: page <= 1 ? 'rgba(255,255,255,0.05)' : 'rgba(59,130,246,0.3)',
                  color: page <= 1 ? '#6b7280' : '#60a5fa',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: page <= 1 ? 'not-allowed' : 'pointer',
                }}
              >
                ← Prev
              </button>
              <span style={{ fontSize: '13px', color: '#9ca3af' }}>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  border: 'none',
                  background: page >= totalPages ? 'rgba(255,255,255,0.05)' : 'rgba(59,130,246,0.3)',
                  color: page >= totalPages ? '#6b7280' : '#60a5fa',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                }}
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
